/**
 * PlatformMediaGrid — renders a realistic multi-media grid preview
 * that matches how each social platform actually displays photos and videos.
 *
 * Also exposes PLATFORM_LAYOUT_TIPS for suggested image counts per platform.
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageIcon, ChevronLeft, ChevronRight, Volume2, VolumeX } from 'lucide-react';

/* ── Media item type passed into the grid ────────────────────────── */
export interface PreviewMediaItem {
  url: string;
  type: 'image' | 'video';
}

/* ── Fallback placeholder ─────────────────────────────────────────── */
const Placeholder: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center ${className}`}>
    <ImageIcon className="w-6 h-6 text-gray-300" />
  </div>
);

/* ── Safe image wrapper ───────────────────────────────────────────── */
const Img: React.FC<{ src: string; className?: string; style?: React.CSSProperties }> = ({ src, className = '', style }) => {
  const [failed, setFailed] = useState(false);
  if (failed || !src) { return <Placeholder className={className} />; }
  return <img src={src} alt="" className={className} style={style} onError={() => setFailed(true)} />;
};

/* ── Inline video player — autoplay, muted, loop like social feeds ── */
const Vid: React.FC<{ src: string; className?: string; style?: React.CSSProperties }> = ({ src, className = '', style }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  const [muted, setMuted] = useState(true);

  // Only autoplay when visible to keep memory use bounded when multiple
  // preview grids render simultaneously across platform tabs.
  useEffect(() => {
    const el = containerRef.current;
    const video = videoRef.current;
    if (!el || !video) { return; }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            video.play().catch(() => { /* ignore autoplay rejections */ });
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.25 }
    );
    observer.observe(el);
    return () => { observer.disconnect(); };
  }, []);

  if (failed || !src) { return <Placeholder className={className} />; }

  return (
    <div ref={containerRef} className={`relative group ${className}`} style={style}>
      <video
        ref={videoRef}
        src={src}
        className="w-full h-full object-cover"
        loop
        muted={muted}
        playsInline
        preload="metadata"
        onError={() => setFailed(true)}
      >
        <track kind="captions" />
      </video>
      {/* Mute toggle */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setMuted((m) => !m); }}
        className="absolute bottom-2 right-2 w-6 h-6 rounded-full bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        aria-label={muted ? 'Unmute' : 'Mute'}
      >
        {muted ? (
          <VolumeX className="w-3 h-3 text-white" />
        ) : (
          <Volume2 className="w-3 h-3 text-white" />
        )}
      </button>
    </div>
  );
};

/* ── Universal media renderer ────────────────────────────────────── */
const Media: React.FC<{ item: PreviewMediaItem; className?: string; style?: React.CSSProperties }> = ({ item, className, style }) => {
  if (item.type === 'video') {
    return <Vid src={item.url} className={className} style={style} />;
  }
  return <Img src={item.url} className={className} style={style} />;
};

/* ── Platform aspect ranges ───────────────────────────────────────── */
/**
 * Per-platform allowed aspect ratio range for single-image feed posts.
 *
 * Mirrors the backend source of truth at
 * `backend/app/services/validation/preflight.py:PLATFORM_RULES`. Values are
 * width/height (e.g. 0.8 = 4:5 portrait, 1.91 = 1.91:1 landscape).
 *
 * Kept in sync by hand because these are platform-mandated limits and
 * change rarely. If you update one side, update the other.
 */
const PLATFORM_ASPECT_RANGE: Record<string, { min: number; max: number }> = {
  instagram: { min: 0.8, max: 1.91 },       // 4:5 → 1.91:1
  facebook: { min: 9 / 16, max: 1.91 },     // 9:16 → 1.91:1
  twitter: { min: 9 / 16, max: 16 / 9 },    // 9:16 → 16:9
  linkedin: { min: 0.5, max: 1.91 },        // vertical to wide
  tiktok: { min: 9 / 16, max: 9 / 16 },     // strict 9:16
  youtube: { min: 9 / 16, max: 9 / 16 },    // Shorts default
  pinterest: { min: 0.5, max: 1.0 },        // portrait only
  bluesky: { min: 9 / 16, max: 16 / 9 },
};

/** Default aspect used until the media has finished loading. */
const FALLBACK_ASPECT = 1;

/**
 * Clamp a natural aspect to the platform's allowed range. Returns both
 * the resolved aspect (what the frame will be) and whether the image
 * needed to be padded — callers use this to decide object-cover vs.
 * object-contain and whether to paint a dark bg behind the image.
 */
function resolveFrameAspect(
  platform: string,
  naturalAspect: number,
): { aspect: number; padded: boolean } {
  const range = PLATFORM_ASPECT_RANGE[platform];
  if (!range) {
    return { aspect: naturalAspect, padded: false };
  }
  if (naturalAspect < range.min) {
    return { aspect: range.min, padded: true };
  }
  if (naturalAspect > range.max) {
    return { aspect: range.max, padded: true };
  }
  return { aspect: naturalAspect, padded: false };
}

/**
 * Probe the media's natural aspect ratio. Returns null while loading so
 * the caller can hold the fallback frame (avoids a layout flash).
 */
function useNaturalAspect(item: PreviewMediaItem): number | null {
  const [aspect, setAspect] = useState<number | null>(null);
  useEffect(() => {
    setAspect(null);
    if (!item.url) {
      return;
    }
    let cancelled = false;
    if (item.type === 'video') {
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.onloadedmetadata = () => {
        if (cancelled) {
          return;
        }
        if (v.videoWidth > 0 && v.videoHeight > 0) {
          setAspect(v.videoWidth / v.videoHeight);
        }
      };
      v.onerror = () => { /* keep fallback */ };
      v.src = item.url;
      return () => {
        cancelled = true;
        v.onloadedmetadata = null;
        v.onerror = null;
        v.removeAttribute('src');
        v.load();
      };
    }
    const img = new Image();
    img.onload = () => {
      if (cancelled) {
        return;
      }
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        setAspect(img.naturalWidth / img.naturalHeight);
      }
    };
    img.onerror = () => { /* keep fallback */ };
    img.src = item.url;
    return () => {
      cancelled = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [item.url, item.type]);
  return aspect;
}

/**
 * SmartFrame — renders a single media item inside a frame that matches
 * what the platform will actually publish. The frame's aspect ratio is
 * the media's natural ratio clamped to the platform's allowed range; if
 * clamping was needed, the frame paints a dark background and the media
 * renders with `object-contain` so the user sees the exact padding /
 * letterbox the platform will apply. If the media already fits, we use
 * `object-cover` for a clean fill.
 *
 * This replaces hard-coded `aspect-square object-cover` which made
 * landscape images look like broken thumbnails in a giant dark canvas.
 */
const SmartFrame: React.FC<{
  item: PreviewMediaItem;
  platform: string;
  /** Override the clamp range — used for carousels where all slides
      must match the first slide's aspect, not be independently clamped. */
  forcedAspect?: number;
  className?: string;
}> = ({ item, platform, forcedAspect, className = '' }) => {
  const natural = useNaturalAspect(item);
  const effectiveNatural = natural ?? FALLBACK_ASPECT;
  const resolved =
    forcedAspect !== undefined
      ? { aspect: forcedAspect, padded: Math.abs(effectiveNatural - forcedAspect) > 0.01 }
      : resolveFrameAspect(platform, effectiveNatural);
  const fit = resolved.padded ? 'object-contain' : 'object-cover';
  const bg = resolved.padded ? 'bg-black' : '';
  return (
    <div
      className={`w-full overflow-hidden ${bg} ${className}`}
      style={{ aspectRatio: resolved.aspect }}
    >
      <Media item={item} className={`w-full h-full ${fit}`} />
    </div>
  );
};

/* ── Hook: pre-validate media URLs so broken ones don't create empty grid slots ── */
function useValidatedMedia(items: PreviewMediaItem[]): PreviewMediaItem[] {
  const [valid, setValid] = useState<PreviewMediaItem[]>(items);
  const itemsKey = items.map((i) => `${i.type}:${i.url}`).join('|');

  useEffect(() => {
    let cancelled = false;
    const results: Array<{ item: PreviewMediaItem; ok: boolean }> = [];
    // Track probes so we can free them on cleanup (detached media elements
    // otherwise keep decoder memory alive).
    const probes: Array<HTMLVideoElement | HTMLImageElement> = [];

    if (items.length === 0) {
      setValid([]);
      return;
    }

    let pending = items.length;

    const checkDone = () => {
      pending -= 1;
      if (pending === 0 && !cancelled) {
        setValid(results.filter((r) => r.ok).map((r) => r.item));
      }
    };

    items.forEach((item) => {
      if (item.type === 'video') {
        // For video, probe with a <video> element
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => { results.push({ item, ok: true }); checkDone(); };
        video.onerror = () => { results.push({ item, ok: false }); checkDone(); };
        video.src = item.url;
        probes.push(video);
      } else {
        // For images, probe with Image()
        const img = new Image();
        img.onload = () => { results.push({ item, ok: true }); checkDone(); };
        img.onerror = () => { results.push({ item, ok: false }); checkDone(); };
        img.src = item.url;
        probes.push(img);
      }
    });

    return () => {
      cancelled = true;
      probes.forEach((el) => {
        el.onload = null;
        el.onerror = null;
        if (el instanceof HTMLVideoElement) {
          el.onloadedmetadata = null;
          el.pause();
        }
        // Clearing src lets the browser release the associated buffers.
        el.removeAttribute('src');
        if (el instanceof HTMLVideoElement) { el.load(); }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsKey]);

  return valid;
}

/* ── Layout tips shown in the composer ────────────────────────────── */
export interface LayoutTip {
  count: number;
  label: string;
  description: string;
}

export const PLATFORM_LAYOUT_TIPS: Record<string, LayoutTip[]> = {
  facebook: [
    { count: 1, label: '1 image', description: 'Full-width hero. Best for a single strong visual.' },
    { count: 2, label: '2 images', description: 'Side-by-side split. Good for before/after or comparison.' },
    { count: 3, label: '3 images', description: 'One large + two small. Great for a featured image with supporting shots.' },
    { count: 4, label: '4 images', description: 'Clean 2x2 grid. Ideal for product showcases or collages.' },
    { count: 5, label: '5 images', description: 'Two large + three small. Perfect for storytelling with variety.' },
  ],
  instagram: [
    { count: 1, label: '1 image', description: 'Square or 4:5 portrait. Maximum feed presence.' },
    { count: 2, label: '2 slides', description: 'Carousel with swipe. Great for tips or reveals.' },
    { count: 5, label: '5 slides', description: 'Multi-slide carousel. Ideal for educational content or listicles.' },
    { count: 10, label: '10 slides', description: 'Maximum carousel. Best for detailed stories or photo dumps.' },
  ],
  twitter: [
    { count: 1, label: '1 image', description: 'Single 16:9 landscape. Maximum engagement.' },
    { count: 2, label: '2 images', description: 'Side-by-side. Good for comparisons.' },
    { count: 3, label: '3 images', description: 'One large + two stacked. Great visual variety.' },
    { count: 4, label: '4 images', description: 'Clean 2x2 grid. Standard multi-image tweet.' },
  ],
  linkedin: [
    { count: 1, label: '1 image', description: 'Full-width landscape. Professional and clean.' },
    { count: 2, label: '2 images', description: 'Side-by-side split. Works for data comparisons.' },
    { count: 4, label: '4 images', description: '2x2 grid. Good for announcements or team photos.' },
  ],
};

/* ── Grid Props ───────────────────────────────────────────────────── */
interface PlatformMediaGridProps {
  platform: string;
  /** @deprecated Use `items` instead for image+video support */
  urls?: string[];
  /** Media items with type info (image or video) */
  items?: PreviewMediaItem[];
  className?: string;
}

/* ── Facebook grid layout ─────────────────────────────────────────── */
const FacebookGrid: React.FC<{ items: PreviewMediaItem[] }> = ({ items }) => {
  const count = items.length;
  const remaining = count > 5 ? count - 5 : 0;
  const shown = items.slice(0, 5);

  if (count === 1) {
    return <SmartFrame item={shown[0]} platform="facebook" />;
  }

  if (count === 2) {
    return (
      <div className="grid grid-cols-2 gap-0.5">
        <Media item={shown[0]} className="w-full aspect-square object-cover" />
        <Media item={shown[1]} className="w-full aspect-square object-cover" />
      </div>
    );
  }

  if (count === 3) {
    return (
      <div className="grid grid-cols-2 gap-0.5">
        <Media item={shown[0]} className="w-full aspect-square object-cover col-span-2" />
        <Media item={shown[1]} className="w-full aspect-square object-cover" />
        <Media item={shown[2]} className="w-full aspect-square object-cover" />
      </div>
    );
  }

  if (count === 4) {
    return (
      <div className="grid grid-cols-2 gap-0.5">
        <Media item={shown[0]} className="w-full aspect-square object-cover" />
        <Media item={shown[1]} className="w-full aspect-square object-cover" />
        <Media item={shown[2]} className="w-full aspect-square object-cover" />
        <Media item={shown[3]} className="w-full aspect-square object-cover" />
      </div>
    );
  }

  // 5+ images: 2 on top, 3 on bottom (with +N overlay on last)
  return (
    <div className="grid grid-cols-6 gap-0.5">
      <Media item={shown[0]} className="col-span-3 w-full aspect-[4/3] object-cover" />
      <Media item={shown[1]} className="col-span-3 w-full aspect-[4/3] object-cover" />
      <Media item={shown[2]} className="col-span-2 w-full aspect-square object-cover" />
      <Media item={shown[3]} className="col-span-2 w-full aspect-square object-cover" />
      <div className="col-span-2 relative">
        <Media item={shown[4]} className="w-full aspect-square object-cover" />
        {remaining > 0 && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="text-white text-lg font-bold">+{remaining}</span>
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Twitter/X grid layout ────────────────────────────────────────── */
const TwitterGrid: React.FC<{ items: PreviewMediaItem[] }> = ({ items }) => {
  const count = items.length;
  const shown = items.slice(0, 4);

  if (count === 1) {
    return (
      <div className="rounded-xl overflow-hidden">
        <SmartFrame item={shown[0]} platform="twitter" />
      </div>
    );
  }

  if (count === 2) {
    return (
      <div className="grid grid-cols-2 gap-0.5 rounded-xl overflow-hidden">
        <Media item={shown[0]} className="w-full aspect-[3/4] object-cover" />
        <Media item={shown[1]} className="w-full aspect-[3/4] object-cover" />
      </div>
    );
  }

  if (count === 3) {
    return (
      <div className="grid grid-cols-2 gap-0.5 rounded-xl overflow-hidden" style={{ height: '280px' }}>
        <Media item={shown[0]} className="w-full h-full object-cover row-span-2" />
        <Media item={shown[1]} className="w-full object-cover" style={{ height: '139px' }} />
        <Media item={shown[2]} className="w-full object-cover" style={{ height: '139px' }} />
      </div>
    );
  }

  // 4 images: 2x2
  return (
    <div className="grid grid-cols-2 gap-0.5 rounded-xl overflow-hidden">
      <Media item={shown[0]} className="w-full aspect-video object-cover" />
      <Media item={shown[1]} className="w-full aspect-video object-cover" />
      <Media item={shown[2]} className="w-full aspect-video object-cover" />
      <Media item={shown[3]} className="w-full aspect-video object-cover" />
    </div>
  );
};

/* ── LinkedIn grid layout ─────────────────────────────────────────── */
const LinkedInGrid: React.FC<{ items: PreviewMediaItem[] }> = ({ items }) => {
  const count = items.length;
  const shown = items.slice(0, 9);
  const remaining = count > 5 ? count - 5 : 0;

  if (count === 1) {
    return <SmartFrame item={shown[0]} platform="linkedin" />;
  }

  if (count === 2) {
    return (
      <div className="grid grid-cols-2 gap-0.5">
        <Media item={shown[0]} className="w-full aspect-square object-cover" />
        <Media item={shown[1]} className="w-full aspect-square object-cover" />
      </div>
    );
  }

  if (count <= 4) {
    return (
      <div className="grid grid-cols-2 gap-0.5">
        {shown.map((item, i) => (
          <Media key={i} item={item} className="w-full aspect-square object-cover" />
        ))}
      </div>
    );
  }

  // 5+ images: similar to Facebook 5+
  return (
    <div className="grid grid-cols-6 gap-0.5">
      <Media item={shown[0]} className="col-span-3 w-full aspect-[4/3] object-cover" />
      <Media item={shown[1]} className="col-span-3 w-full aspect-[4/3] object-cover" />
      <Media item={shown[2]} className="col-span-2 w-full aspect-square object-cover" />
      <Media item={shown[3]} className="col-span-2 w-full aspect-square object-cover" />
      <div className="col-span-2 relative">
        <Media item={shown[4]} className="w-full aspect-square object-cover" />
        {remaining > 0 && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="text-white text-lg font-bold">+{remaining}</span>
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Instagram Carousel ──────────────────────────────────────────── */
const InstagramCarousel: React.FC<{ items: PreviewMediaItem[] }> = ({ items }) => {
  const [current, setCurrent] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Instagram enforces a single aspect ratio across all carousel slides.
  // Derive it from the FIRST slide's natural aspect, clamped to IG's
  // allowed range, then apply that to every slide so the preview matches
  // what actually publishes. Falls back to 1:1 while metadata loads.
  const firstAspect = useNaturalAspect(items[0]);
  const carouselAspect = resolveFrameAspect(
    'instagram',
    firstAspect ?? FALLBACK_ASPECT,
  ).aspect;

  const scrollTo = useCallback((index: number) => {
    const clamped = Math.max(0, Math.min(index, items.length - 1));
    setCurrent(clamped);
    if (scrollRef.current) {
      const child = scrollRef.current.children[clamped] as HTMLElement | undefined;
      if (child) {
        child.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
    }
  }, [items.length]);

  const handleScroll = useCallback(() => {
    if (!scrollRef.current) { return; }
    const container = scrollRef.current;
    const slideWidth = container.offsetWidth;
    if (slideWidth > 0) {
      const idx = Math.round(container.scrollLeft / slideWidth);
      setCurrent(idx);
    }
  }, []);

  return (
    <div className="relative">
      {/* Scrollable slides */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {items.map((item, i) => (
          <div key={i} className="flex-shrink-0 w-full snap-start">
            <SmartFrame
              item={item}
              platform="instagram"
              forcedAspect={carouselAspect}
            />
          </div>
        ))}
      </div>

      {/* Slide counter badge */}
      {items.length > 1 && (
        <div className="absolute top-2 right-2 bg-black/60 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">
          {current + 1}/{items.length}
        </div>
      )}

      {/* Navigation arrows */}
      {items.length > 1 && current > 0 && (
        <button
          type="button"
          onClick={() => scrollTo(current - 1)}
          className="absolute left-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white/80 shadow flex items-center justify-center hover:bg-white transition-colors"
          aria-label="Previous slide"
        >
          <ChevronLeft className="w-3.5 h-3.5 text-slate-700" />
        </button>
      )}
      {items.length > 1 && current < items.length - 1 && (
        <button
          type="button"
          onClick={() => scrollTo(current + 1)}
          className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white/80 shadow flex items-center justify-center hover:bg-white transition-colors"
          aria-label="Next slide"
        >
          <ChevronRight className="w-3.5 h-3.5 text-slate-700" />
        </button>
      )}

      {/* Dot indicators */}
      {items.length > 1 && items.length <= 10 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1">
          {items.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => scrollTo(i)}
              className={`w-1.5 h-1.5 rounded-full transition-colors ${
                i === current ? 'bg-blue-500' : 'bg-white/60'
              }`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Main Component ───────────────────────────────────────────────── */
export const PlatformMediaGrid: React.FC<PlatformMediaGridProps> = ({
  platform,
  urls: rawUrls,
  items: rawItems,
  className = '',
}) => {
  // Support both legacy `urls` (string[]) and new `items` (PreviewMediaItem[])
  const legacyItems: PreviewMediaItem[] = (rawUrls || []).map((url) => ({ url, type: 'image' as const }));
  const inputItems = rawItems && rawItems.length > 0 ? rawItems : legacyItems;
  const items = useValidatedMedia(inputItems);

  if (!items.length) { return null; }

  // Single media — clamp image's natural aspect to the platform's
  // allowed range so the preview reflects what will actually publish.
  // Landscape photos no longer get jammed into a hard-coded square
  // frame; portrait photos on TikTok are allowed to be shown as 9:16
  // with padding instead of over-cropped.
  if (items.length === 1) {
    switch (platform) {
      case 'instagram':
        return <SmartFrame item={items[0]} platform="instagram" className={className} />;
      case 'tiktok':
        return <SmartFrame item={items[0]} platform="tiktok" className={className} />;
      default:
        break; // fall through to platform-specific grid
    }
  }

  switch (platform) {
    case 'facebook':
      return <div className={className}><FacebookGrid items={items} /></div>;
    case 'twitter':
      return <div className={className}><TwitterGrid items={items} /></div>;
    case 'linkedin':
      return <div className={className}><LinkedInGrid items={items} /></div>;
    case 'instagram':
      return <div className={className}><InstagramCarousel items={items} /></div>;
    default:
      // Fallback: Facebook-style grid
      return <div className={className}><FacebookGrid items={items} /></div>;
  }
};

/* ── Layout Tips Panel ────────────────────────────────────────────── */
interface LayoutTipsPanelProps {
  platform: string;
  currentCount: number;
}

export const LayoutTipsPanel: React.FC<LayoutTipsPanelProps> = ({
  platform,
  currentCount,
}) => {
  const { t } = useTranslation();
  const tips = PLATFORM_LAYOUT_TIPS[platform];
  if (!tips) { return null; }

  const platformLabels: Record<string, string> = {
    facebook: 'Facebook',
    instagram: 'Instagram',
    twitter: 'X / Twitter',
    linkedin: 'LinkedIn',
  };

  return (
    <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 space-y-2">
      <h4 className="text-xs font-semibold text-blue-900">
        {t('layout_tips_title', '{{platform}} layout tips', { platform: platformLabels[platform] || platform })}
      </h4>
      <div className="space-y-1.5">
        {tips.map((tip) => (
          <div
            key={tip.count}
            className={`flex items-start gap-2 text-[11px] rounded px-2 py-1.5 transition ${
              tip.count === currentCount
                ? 'bg-blue-100 text-blue-900 font-medium'
                : 'text-blue-800 hover:bg-blue-100/50'
            }`}
          >
            <span className="font-bold text-blue-600 min-w-[2rem]">{tip.count}x</span>
            <span>{tip.description}</span>
          </div>
        ))}
      </div>
      {currentCount > 0 && (
        <p className="text-[10px] text-blue-600 pt-1 border-t border-blue-100">
          {t('layout_tips_current', 'You have {{count}} image(s) attached', { count: currentCount })}
        </p>
      )}
    </div>
  );
};

export default PlatformMediaGrid;
